// useMietkonto(objektId) – das gemeinsame Mietkonto für alle Ansichten eines Objekts.
// Nach jeder Buchung meldet MIETKONTO_GEAENDERT alle offenen Ansichten zum Neuladen,
// damit Mietkonto, Mieteingang, Mahnwesen, Bankabgleich und Saldo dieselben Zahlen zeigen.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { heuteIso } from '../immobilien/format';
import { ladeMietkontoDaten, type MietkontoLaden } from './api';
import { baueMietkonto } from './mietkonto';
import type { Mietkonto } from './typen';

export const MIETKONTO_GEAENDERT = 'immo-mietkonto-geaendert';

export function meldeMietkontoGeaendert(objektId: string): void {
  window.dispatchEvent(new CustomEvent<string>(MIETKONTO_GEAENDERT, { detail: objektId }));
}

export type MietkontoZustand =
  | { status: 'laedt' }
  | { status: 'nicht_eingerichtet' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; konto: Mietkonto; laden: Extract<MietkontoLaden, { status: 'ok' }> };

export function useMietkonto(objektId: string | null): { zustand: MietkontoZustand; neuLaden: () => void } {
  const [laden, setLaden] = useState<MietkontoLaden | null>(null);
  const [runde, setRunde] = useState(0);
  const neuLaden = useCallback(() => setRunde(r => r + 1), []);

  useEffect(() => {
    if (!objektId) return;
    let aktiv = true;
    ladeMietkontoDaten(objektId)
      .then(l => { if (aktiv) setLaden(l); })
      .catch((e: unknown) => { if (aktiv) setLaden({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [objektId, runde]);

  useEffect(() => {
    if (!objektId) return;
    const bei = (e: Event) => { const id = (e as CustomEvent<string>).detail; if (!id || id === objektId) neuLaden(); };
    window.addEventListener(MIETKONTO_GEAENDERT, bei);
    return () => window.removeEventListener(MIETKONTO_GEAENDERT, bei);
  }, [objektId, neuLaden]);

  const heute = heuteIso();
  const zustand = useMemo<MietkontoZustand>(() => {
    if (!laden) return { status: 'laedt' };
    if (laden.status !== 'ok') return laden;
    return { status: 'ok', laden, konto: baueMietkonto({ ...laden.daten, heute }) };
  }, [laden, heute]);

  return { zustand, neuLaden };
}
